// ====================================================
// src/components/block/liveStreamManagement/enchainements/Enchainement.ts
//
// Base commune à tous les enchaînements du T-bar de la régie (fondu, et ceux
// qui suivront). Un nouvel enchaînement s'ajoute en créant une classe qui
// étend Enchainement et en l'inscrivant dans registry.ts — le T-bar, lui, ne
// connaît que cette interface : il pousse un `progress` (0 = flux "final"
// actuel plein cadre, 1 = flux "preview" a pris l'antenne) et laisse
// l'enchaînement actif décider comment dessiner l'image intermédiaire.
//
// Même patron que VideoEffect (../videoEffects/VideoEffect.ts) pour le
// panneau d'effets vidéo : classe abstraite + sous-classes + registry.
// ====================================================

import React from 'react';

export interface EnchainementDrawRect { x: number; y: number; w: number; h: number; }

export interface EnchainementRenderInput {
  ctx: CanvasRenderingContext2D;
  /** Flux "final" actuel, déjà à l'antenne. */
  currentSource: CanvasImageSource;
  /** Rectangle où `currentSource` doit être dessiné (letterboxing déjà résolu). */
  currentRect: EnchainementDrawRect;
  /** Flux "preview" qui prend la main. */
  nextSource: CanvasImageSource;
  /** Rectangle où `nextSource` doit être dessiné (letterboxing déjà résolu). */
  nextRect: EnchainementDrawRect;
  /** 0 = currentSource plein cadre, 1 = nextSource a pris l'antenne. */
  progress: number;
  width: number;
  height: number;
}

export interface EnchainementControlProps<TConfig> {
  config: TConfig;
  onChange: (config: TConfig) => void;
}

export abstract class Enchainement<TConfig = unknown> {
  abstract readonly id: string;
  abstract readonly label: string;
  /** Nom passé au composant Icon partagé (cf. DeckV2.tsx, bouton "Transition"). */
  abstract readonly icon: string;
  abstract readonly defaultConfig: TConfig;

  /** Dessine l'image de transition pour `input.progress` dans `input.ctx`. */
  abstract render(input: EnchainementRenderInput, config: TConfig): void;

  /** Réglages propres à cet enchaînement (ex. direction pour un futur "wipe"). */
  abstract renderControls(props: EnchainementControlProps<TConfig>): React.ReactNode;
}
